import { create } from 'zustand';
import apiService from '../services/apiService';

const useMeetingStore = create((set, get) => ({
  meetings: [],
  stats: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },
  filters: {
    startDate: null,
    endDate: null,
    type: 'all',
    status: null
  },

  // Actions
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setFilters: (filters) => set({ filters: { ...get().filters, ...filters } }),
  
  fetchMeetings: async (customFilters = {}) => {
    set({ loading: true, error: null });
    
    try {
      const { filters, pagination } = get();
      
      const queryParams = {
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...filters,
        ...customFilters
      };

      // Remove null/undefined values
      Object.keys(queryParams).forEach(key => {
        if (!queryParams[key] || queryParams[key] === 'null') {
          delete queryParams[key];
        }
      });

      const response = await apiService.get('/meetings', { params: queryParams });

      set({
        meetings: response.data.data.meetings,
        pagination: response.data.data.pagination,
        loading: false
      });
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to fetch meetings',
        loading: false
      });
    }
  },

  fetchMeetingById: async (meetingId) => {
    set({ loading: true, error: null });
    
    try {
      const response = await apiService.get(`/meetings/${meetingId}`);
      return response.data.data;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to fetch meeting',
        loading: false
      });
      throw error;
    }
  },

  updateMeetingStatus: async (meetingId, status, notes = '') => {
    set({ loading: true, error: null });
    
    try {
      const response = await apiService.patch(
        `/meetings/${meetingId}/status`,
        { status, notes }
      );

      // Update the meeting in the local state
      const { meetings } = get();
      const updatedMeetings = meetings.map(meeting =>
        meeting.id === meetingId
          ? { ...meeting, status, notes, updatedAt: new Date().toISOString() }
          : meeting
      );

      set({
        meetings: updatedMeetings,
        loading: false
      });

      return response.data.data;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to update meeting status',
        loading: false
      });
      throw error;
    }
  },

  fetchMeetingStats: async () => {
    try {
      const response = await apiService.get('/meetings/stats');
      set({ stats: response.data.data });
      return response.data.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch meeting stats' });
      throw error;
    }
  },

  setPage: (page) => {
    set({ pagination: { ...get().pagination, page } });
    get().fetchMeetings();
  },

  setLimit: (limit) => {
    set({ pagination: { ...get().pagination, limit, page: 1 } });
    get().fetchMeetings();
  },

  applyFilters: (newFilters) => {
    set({ 
      filters: { ...get().filters, ...newFilters },
      pagination: { ...get().pagination, page: 1 }
    });
    get().fetchMeetings();
  },

  clearFilters: () => {
    set({
      filters: {
        startDate: null,
        endDate: null,
        type: 'all',
        status: null
      },
      pagination: { ...get().pagination, page: 1 }
    });
    get().fetchMeetings();
  }
}));

export default useMeetingStore;
